import { ExternalProps } from './LogPointsAPIsBody'
import { LogPointsService, LogPointStates } from '../../util/LogPointsService'
import { AppAction, Dispatch, ExtraArgs, GetState } from '../../../../types'
import {
  API,
  isSplitMessageLoggingPolicyConfiguration,
  MessageLoggingPolicyConfiguration
} from '../../../../remote-api/models'
import logPointsSelectors from '../../LogPointsPage/selectors'
import { changePlatformToken, selectors as authSelectors } from '../../../../lifecycle/auth'

export enum ActionType {
  FetchLogPoints = 'Monitoring/LogPoints/FetchLogPoints',
  FetchedLogPoints = 'Monitoring/LogPoints/FetchedLogPoints',
  FetchedPolicyConfiguration = 'Monitoring/LogPoints/FetchedPolicyConfiguration',
  SavingLogPoints = 'Monitoring/LogPoints/SavingLogPoints',
  SavedLogPoints = 'Monitoring/LogPoints/SavedLogPoints',
  SavedLogPointsError = 'Monitoring/LogPoints/SavedLogPointsError',
  ChangeAppFullDomain = 'Monitoring/LogPoints/ChangeAppFullDomain',
  ChangeRuntimeVerifiable = 'Monitoring/LogPoints/ChangeRuntimeVerifiable',
  ResetDomainAndRuntime = 'Monitoring/LogPoints/ResetDomainAndRuntime',
  ResetError = 'Monitoring/LogPoints/ResetError'
}

// Basic actions

export type SavingLogPointsAction = {
  type: ActionType.SavingLogPoints
}

export type SavedLogPointsAction = {
  type: ActionType.SavedLogPoints
}

export type SavedLogPointsErrorAction = {
  type: ActionType.SavedLogPointsError,
  payload: string
}

export type FetchLogPointsAction = {
  type: ActionType.FetchLogPoints
}

export type FetchedLogPointsAction = {
  type: ActionType.FetchedLogPoints
  payload: { logPoints: LogPointStates }
}

export type FetchedPolicyConfigurationAction = {
  type: ActionType.FetchedPolicyConfiguration
  payload: { configurations: MessageLoggingPolicyConfiguration[] }
}

export type ChangeAppFullDomainAction = {
  type: ActionType.ChangeAppFullDomain
  payload: { appFullDomain: string }
}

export type ChangeRuntimeVerifiableAction = {
  type: ActionType.ChangeRuntimeVerifiable
  payload: { isRuntimeVerifiable: boolean }
}

export type ResetErrorAction = {
  type: ActionType.ResetError
}

export type ResetDomainAndRuntimeAction = {
  type: ActionType.ResetDomainAndRuntime
}

export type LogPointsAPIAction =
  | FetchLogPointsAction
  | FetchedLogPointsAction
  | FetchedPolicyConfigurationAction
  | SavingLogPointsAction
  | SavedLogPointsAction
  | SavedLogPointsErrorAction
  | ChangeAppFullDomainAction
  | ChangeRuntimeVerifiableAction
  | ResetErrorAction
  | ResetDomainAndRuntimeAction

// Basic Actions Creators

export const savedLogPointsAction = (): AppAction => ({
  type: ActionType.SavedLogPoints
})

export const resetErrorAction = (): AppAction => ({
  type: ActionType.ResetError
})

export const resetDomainAndRuntimeAction = (): AppAction => ({
  type: ActionType.ResetDomainAndRuntime
})

export const savedLogPointsActionError = (error: string): AppAction => ({
  type: ActionType.SavedLogPointsError,
  payload: error
})

export const savingLogPointsAction = (): AppAction => ({
  type: ActionType.SavingLogPoints
})

export const fetchLogPointsAction = (): AppAction => ({
  type: ActionType.FetchLogPoints
})

export const fetchedLogPointsAction = (logPoints: LogPointStates): AppAction => ({
  type: ActionType.FetchedLogPoints,
  payload: {logPoints}
})

export const fetchedPolicyConfigurationAction = (configurations: MessageLoggingPolicyConfiguration[]): AppAction => ({
  type: ActionType.FetchedPolicyConfiguration,
  payload: {configurations}
})

export const changeAppFullDomainAction = (appFullDomain: string): AppAction => ({
  type: ActionType.ChangeAppFullDomain,
  payload: {appFullDomain}
})

export const changeRuntimeVerifiable = (isRuntimeVerifiable: boolean): AppAction => ({
  type: ActionType.ChangeRuntimeVerifiable,
  payload: {isRuntimeVerifiable}
})

// Thunks

export const resetError = () => (dispatch: Dispatch) => {
  dispatch(resetErrorAction())
}

export const resetDomainAndRuntime = () => (dispatch: Dispatch) => {
  dispatch(resetDomainAndRuntimeAction())
}

export const fetchLogPoints = (environmentId: string, versionId: string) => {
  return (dispatch: Dispatch, getState: GetState, {api}: ExtraArgs) => {
    dispatch(fetchLogPointsAction())

    const maybePlatformToken = authSelectors.getPlatformToken(getState())

    if (maybePlatformToken) {
      const remoteAPIManager = api.apiManager(getState)
      const remoteARM = api.arm(getState)
      const logPointsService = new LogPointsService(remoteAPIManager, remoteARM)
      const orgId = getState().monitoring.auth.profile.organizationId

      logPointsService.fetchMessageLoggingConfiguration(orgId, environmentId, versionId)
        .then(configurations => {
          const logPointStates = logPointsService.getLogPointStates(configurations)
          dispatch(fetchedPolicyConfigurationAction(configurations))
          dispatch(fetchedLogPointsAction(logPointStates))
        })
    } else {
      const apiPlatformRemoteAPI = api.apiPlatform(getState)
      apiPlatformRemoteAPI.getAPIPlatformToken()
        .then(session => {
          dispatch(changePlatformToken(session.token))
          dispatch(fetchLogPoints(environmentId, versionId))
        })
    }
  }
}

export const saveLogPoints = (props: ExternalProps, logPoints: LogPointStates) => {
  return (dispatch: Dispatch, getState: GetState, {api}: ExtraArgs) => {
    const maybePlatformToken = authSelectors.getPlatformToken(getState())

    if (maybePlatformToken) {
      const remoteAPIManager = api.apiManager(getState)
      const remoteARM = api.arm(getState)
      const logPointsService = new LogPointsService(remoteAPIManager, remoteARM)
      const orgId = getState().monitoring.auth.profile.organizationId
      const environmentId = props.environmentId
      const apiId = props.versionId
      const versions = getState().monitoring.logPoints.versionSelector.resources

      const saveCallback = () => {
        logPointsService.fetchAPIPolicyBody(orgId, environmentId, apiId, logPoints)
          .then(apiPolicyBody => {
            if (apiPolicyBody) {
              const isSplitPolicy = isSplitMessageLoggingPolicyConfiguration(apiPolicyBody.configurationData)

              let loggingConfiguration = apiPolicyBody.configurationData

              if (isSplitPolicy) {
                loggingConfiguration = apiPolicyBody.configurationData.loggingConfiguration
              }

              if (loggingConfiguration.length > 0) {
                logPointsService.saveLogPoints(orgId, environmentId, apiId, apiPolicyBody)
                  .then(apiPolicy => isSplitPolicy ? apiPolicy.configurationData.loggingConfiguration : apiPolicy.configurationData)
                  .then(newConfigurations => {
                    const logPointStates = logPointsService.getLogPointStates(newConfigurations)
                    dispatch(fetchedPolicyConfigurationAction(newConfigurations))
                    dispatch(fetchedLogPointsAction(logPointStates))
                    dispatch(savedLogPointsAction())
                  })
                  .catch(err => dispatch(savedLogPointsActionError(err)))

              } else if (apiPolicyBody.id) {
                logPointsService.deleteAPIPolicy(orgId, environmentId, apiId, apiPolicyBody.id.toString())
                  .then(() => {
                    const logPointStates = logPointsService.getLogPointStates([])
                    dispatch(fetchedPolicyConfigurationAction([]))
                    dispatch(fetchedLogPointsAction(logPointStates))
                    dispatch(savedLogPointsAction())
                  })
                  .catch(err => dispatch(savedLogPointsActionError(err)))
              }
            }
          })
      }

      dispatch(getApplicationFullDomain(saveCallback)(orgId, environmentId, apiId, versions))
    } else {
      const apiPlatformRemoteAPI = api.apiPlatform(getState)
      apiPlatformRemoteAPI.getAPIPlatformToken()
        .then(session => {
          dispatch(changePlatformToken(session.token))
          dispatch(saveLogPoints(props, logPoints))
        })
    }
  }
}

export const getApplicationFullDomain = (doneCallback?: () => void) => (organizationId: string,
                                                                        environmentId: string,
                                                                        versionId: string,
                                                                        versions?: API[]) => {
  return (dispatch: Dispatch, getState: GetState, {api}: ExtraArgs) => {
    const maybePlatformToken = authSelectors.getPlatformToken(getState())

    if (maybePlatformToken) {
      const versionSelector = logPointsSelectors.versionSelector.getAll(getState())
      const armAPI = api.arm(getState)
      const amAPI = api.apiManager(getState)
      const logPointsService = new LogPointsService(amAPI, armAPI)
      const onFulfilled = () => doneCallback && doneCallback()
      const dispatchChangeAppDomain = (fullDomain: string) => dispatch(changeAppFullDomainAction(fullDomain))
      const resolvedVersions = versions || versionSelector.resources

      logPointsService.getApplicationFullDomain(organizationId, environmentId, versionId, resolvedVersions)
        .then(dispatchChangeAppDomain)
        .then(onFulfilled)
        .catch(onFulfilled)
    } else {
      const apiPlatformRemoteAPI = api.apiPlatform(getState)
      apiPlatformRemoteAPI.getAPIPlatformToken()
        .then(session => {
          dispatch(changePlatformToken(session.token))
          dispatch(getApplicationFullDomain(doneCallback)(organizationId, environmentId, versionId, versions))
        })
    }
  }
}

export const getRuntimeVerifiable = (organizationId: string, environmentId: string, versionId: string) =>
  (dispatch: Dispatch, getState: GetState, {api}: ExtraArgs) => {
    const maybePlatformToken = authSelectors.getPlatformToken(getState())

    if (maybePlatformToken) {
      const armAPI = api.arm(getState)
      const amAPI = api.apiManager(getState)
      const logPointsService = new LogPointsService(amAPI, armAPI)

      logPointsService.fetchAPI(organizationId, environmentId, versionId)
        .then(detailedApi => {
          dispatch(changeRuntimeVerifiable(!LogPointsService.isBasicEndpointAPI(detailedApi)))
        })
    } else {
      const apiPlatformRemoteAPI = api.apiPlatform(getState)
      apiPlatformRemoteAPI.getAPIPlatformToken()
        .then(session => {
          dispatch(changePlatformToken(session.token))
          dispatch(getRuntimeVerifiable(organizationId, environmentId, versionId))
        })
    }
  }
